import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/pro-light-svg-icons';
import { COLORS } from '../settings/variables/Colors';
import { SPACERS } from '../settings/variables/Spacers';
import IndicatorTextareaRender from 'npo/pages/ReportBuilder/Components/ImpactIndicator/IndicatorTextareaRender';
import TrueImpactFileUploadBox, {
  TrueImpactFile,
} from './TrueImpactFileUploadBox';
import { StyledTrueImpactText } from 'npo/pages/ReportBuilder/Components/ImpactIndicator/IndicatorTextarea';

export const indicatorAcceptedFileString = 'DOC, DOCX, PDF, XLSX, CSV';
export const indicatorAllowedFileExtensions = [
  'text/csv',
  'application/pdf',
  'application/msword',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];
export const indicatorMaxFileSize = 512 * 1000 * 1000; // 512mb

interface Props {
  files: TrueImpactFile[];
  maxFileSize: number;
  autoFocus?: boolean;
  placeholder?: string;
  acceptedFileString: string;
  allowedFileExtensions: string[];
  className?: string;
  handleFileUpload: (file: File | undefined) => unknown;
  handleDelete: (fileName: string) => void;
  setFiles: React.Dispatch<React.SetStateAction<TrueImpactFile[]>>;
  description?: string;
  setDescription: (val: string) => void;
  isUploading?: boolean;
  errorMessage?: string;
  maxChars?: number;
  questionId?: number;
  fullTextareaWidth?: boolean;
}

const StyledDiv = styled.div<{ fullTextareaWidth: boolean }>`
  display: grid;
  grid-template-columns: 364px 278px;
  gap: 26px;
  ${({ fullTextareaWidth }) =>
    fullTextareaWidth && 'display: block; max-width: 600px; margin: auto;'}
  textarea {
    width: 100%;
    resize: vertical;
    font-size: 16px;
    border: transparent;
    padding: ${SPACERS.small};
    background-color: ${COLORS.grayLightest};
  }
  .uploaded-files {
    display: flex;
    flex-direction: column;
    gap: ${SPACERS.small};
    border: 1px dashed ${COLORS.ctaPrimarySubdued};
    border-radius: 4px;
    padding: 10px;
    max-height: 114px;
    overflow-y: auto;

    /* Scrollbar styles */
    scrollbar-width: thin;
    scrollbar-color: ${COLORS.ctaPrimary} transparent;

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      background-color: ${COLORS.ctaPrimary};
      border-radius: 3px;
    }
  }
  .file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 6px 8px;
    border: 1px solid ${COLORS.ctaPrimarySubdued};
    border-radius: 4px;
  }
  .file-name {
    font-size: 14px;
    font-weight: 500;
    max-width: 150px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
    text-align: left;
  }
  .delete-btn {
    cursor: pointer;
    color: ${COLORS.blackPure};
  }
  i {
    font-style: italic;
    margin-top: 4px;
  }
  .icon-d {
    padding: 4px;
    border-radius: 50%;
    background-color: ${COLORS.criticalSubdued};
    color: ${COLORS.whiteBase};
    font-size: 12px;
  }
`;

const RenderFile: React.FC<{
  fileName: string | undefined;
  handleDelete: (fileName: string) => unknown;
}> = ({ fileName, handleDelete }) => {
  if (!fileName || typeof fileName !== 'string') return null;
  return (
    <div key={fileName} className="file-item">
      <span className="file-name">{fileName}</span>
      <span className="delete-btn" onClick={() => handleDelete(fileName)}>
        <FontAwesomeIcon icon={faTrash} className="icon-d" />
      </span>
    </div>
  );
};

const TrueImpactFileUploadWithNotes: React.FC<Props> = ({
  files,
  autoFocus,
  maxFileSize,
  acceptedFileString,
  allowedFileExtensions,
  placeholder,
  className,
  handleFileUpload,
  handleDelete,
  setFiles,
  description,
  setDescription,
  isUploading = false,
  errorMessage,
  maxChars,
  questionId,
  fullTextareaWidth = false,
}) => {
  const [error, setError] = useState<string | undefined>(undefined);
  const [formatError, setFormatError] = useState<string | undefined>(undefined);
  const onFileUpload = useCallback(
    async (file: File | undefined) => {
      if (files?.length >= 10) return;
      if (file && !allowedFileExtensions.includes(file.type)) {
        setFormatError(
          `That file type isn’t supported. Accepted formats: ${acceptedFileString}.`,
        );
        return;
      }
      setFormatError(undefined);
      handleFileUpload(file);
    },
    [
      files?.length || 0,
      handleFileUpload,
      allowedFileExtensions,
      acceptedFileString,
    ],
  );

  useEffect(() => {
    if (typeof setFiles === 'function') {
      setFiles(prev =>
        Array.isArray(prev)
          ? prev.map(f =>
              f && typeof f === 'object' && !Array.isArray(f)
                ? { ...f, description: description ?? '' }
                : f,
            )
          : [],
      );
    }
  }, [setFiles, setDescription]);

  return (
    <StyledDiv className={className} fullTextareaWidth={fullTextareaWidth}>
      <StyledTrueImpactText
        variant="body"
        textareaBackgroundColor={'whiteBase'}
      >
        <IndicatorTextareaRender
          id={'description-box'}
          questionId={questionId}
          handleSave={e => {
            setDescription(e);
            setError(e.length < 1 ? 'Description is required' : '');
          }}
          placeholder={placeholder}
          initialValue={description}
          textareaClassName={error ? 'textarea-error' : undefined}
          maxLength={maxChars}
        />
      </StyledTrueImpactText>
      <div>
        {files?.length < 10 && (
          <TrueImpactFileUploadBox
            acceptedFileString={acceptedFileString}
            file={undefined}
            handleFileUpload={onFileUpload}
            isUploading={isUploading}
          />
        )}
        {!files || (files?.length === 0 && <i>Optional</i>)}
        {(errorMessage || formatError) && (
          <p style={{ color: COLORS.critical, marginTop: 8, fontSize: 14 }}>
            {errorMessage || formatError}
          </p>
        )}
        {files?.length > 0 && (
          <div className="uploaded-files">
            {files?.map(file => {
              if (Array.isArray(file.fileName)) {
                return file.fileName.map(f => (
                  <RenderFile
                    key={f.fileName}
                    fileName={f.fileName}
                    handleDelete={handleDelete}
                  />
                ));
              }
              return (
                <RenderFile
                  key={file.fileName}
                  fileName={file.fileName}
                  handleDelete={handleDelete}
                />
              );
            })}
          </div>
        )}
      </div>
    </StyledDiv>
  );
};

export const indicatorFileUploadValidations = ({
  file,
  maxFileSize = indicatorMaxFileSize,
  allowedFileExtensions = indicatorAllowedFileExtensions,
  handleFileUpload,
  setFile,
}: {
  file?: File;
  maxFileSize?: number;
  allowedFileExtensions?: string[];
  handleFileUpload: (file: File | undefined) => unknown;
  setFile: React.Dispatch<React.SetStateAction<TrueImpactFile | undefined>>;
}) => {
  if (!file) {
    handleFileUpload(undefined);
    return;
  }
  if (!allowedFileExtensions.includes(file.type)) {
    setFile((s: TrueImpactFile | undefined) => ({
      ...s,
      file: undefined,
      fileName: undefined,
      error: `Sorry, ${file.type || 'that'} file format isn’t supported.`,
    }));
    return;
  }
  if (file.size > maxFileSize) {
    setFile((s: TrueImpactFile | undefined) => ({
      ...s,
      file: undefined,
      fileName: undefined,
      error: `File is too big. Please try size under ${maxFileSize}MB`,
    }));
    return;
  }
  return file;
};

export default TrueImpactFileUploadWithNotes;
